import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  adminOpsApi,
  type AccountDeletionWebRequestItem,
  type AccountDeletionWebRequestStatus,
  type ApprovalDecision,
  type AuditEventItem,
  type AuditEventParams,
  type AuditLegalHoldItem,
  type AuditLegalHoldParams,
  type AuditPruneApprovalItem,
  type AuditPruneApprovalParams,
  type AuditPruneDecision,
  type ExtendAuditLegalHoldPayload,
  type PlaceAuditLegalHoldPayload,
  type ReleaseAuditLegalHoldPayload,
  type BroadcastItem,
  type BroadcastStatus,
  type CharityVerificationDecisionPayload,
  type CharityVerificationListParams,
  type CharityVerificationQueueItem,
  type CreateBroadcastPayload,
  type UpdateBroadcastPayload,
  type DeletionRequestItem,
  type EnforcementAction,
  type ExemptionBasis,
  type FailedOutboxItem,
  type FlaggedContentItem,
  type FraudReviewDonorItem,
  type IrsReviewResolution,
  type IrsSyncReviewItem,
  type ListEnvelope,
  type ListParams,
  type ModerationReportItem,
  type OcrManualReviewItem,
  type OcrReviewHoldStatus,
  type OrgStatusUnacknowledgedItem,
  type PendingActionItem,
  type QuarantineItem,
  type CreateReferralCodePayload,
  type ReferralCodeStatusFilter,
  type ReferralCohortParams,
  type ResolveFraudReviewPayload,
  type StrandedAvatarItem,
  type SupportRequestItem,
  type SupportStatus,
  type EarlyAccessLeadItem,
  type EarlyAccessLeadParams,
  type EarlyAccessLeadsSummary,
  type FoundingPartnerRequestItem,
  type FoundingPartnerRequestParams,
  type FoundingPartnerRequestsSummary,
  type FoundingPartnerStatus,
  type ImportWebsiteLeadOutreachPayload,
  type LogWebsiteLeadOutreachPayload,
  type SummarizedListEnvelope,
  type UserDeletionReason,
  type UserReportResolutionAction,
  type WebsiteLeadType,
} from '../api/adminOps';
import { useToast } from '../components/ui/Toast';
import {
  APPROVAL_LOOKUP_PAGE_SIZE,
  findPendingApproval,
  walkPendingApprovals,
} from '../pages/broadcasts.logic';
import { decisionSuccessMessage } from '../pages/charityVerificationDecision.logic';
import { userReportResolutionMessage } from '../pages/moderation.logic';
import {
  PENDING_DELETION_LOOKUP_EXHAUSTED,
  pendingUserDeletionFor,
} from '../pages/userDeletion.logic';
import { importResultLine } from '../pages/websiteLeads.logic';

export type AdminQueueName =
  | 'moderation-reports'
  | 'moderation-flagged'
  | 'audit-events'
  | 'approvals'
  | 'irs-reviews'
  | 'deletion-requests'
  | 'support-requests'
  | 'quarantine'
  | 'outbox-failed'
  | 'avatars-stranded'
  | 'org-status'
  | 'ocr-manual-review'
  | 'fraud-review'
  | 'broadcasts'
  | 'account-deletion-requests'
  | 'early-access-leads'
  | 'founding-partner-requests'
  | 'audit-legal-holds'
  | 'audit-prune-approvals'
  | 'charity-verifications';

export const adminOpsKeys = {
  all: ['adminOps'] as const,
  overview: () => [...adminOpsKeys.all, 'overview'] as const,
  queues: () => [...adminOpsKeys.all, 'queue'] as const,
  queue: (name: AdminQueueName) => [...adminOpsKeys.queues(), name] as const,
  queuePage: (name: AdminQueueName, params: unknown) =>
    [...adminOpsKeys.queue(name), params] as const,
  compliance: () => [...adminOpsKeys.all, 'compliance'] as const,
  enforcement: (userId: string) => [...adminOpsKeys.all, 'enforcement', userId] as const,
  fraudReviewDonor: (userId: string) =>
    [...adminOpsKeys.all, 'fraud-review-donor', userId] as const,
  broadcast: (id: string) => [...adminOpsKeys.all, 'broadcast', id] as const,
  broadcastAudience: (audience: string) =>
    [...adminOpsKeys.all, 'broadcast-audience', audience] as const,
  broadcastApproval: (id: string) => [...adminOpsKeys.all, 'broadcast-approval', id] as const,
  userDeletion: (userId: string) => [...adminOpsKeys.all, 'user-deletion', userId] as const,
  userDeletionApproval: (userId: string) =>
    [...adminOpsKeys.all, 'user-deletion-approval', userId] as const,
  referralCodes: () => [...adminOpsKeys.all, 'referral-codes'] as const,
  referralCodeList: (status: ReferralCodeStatusFilter) =>
    [...adminOpsKeys.referralCodes(), status] as const,
  referralCohorts: () => [...adminOpsKeys.all, 'referral-cohorts'] as const,
  referralCohortReport: (params: ReferralCohortParams) =>
    [...adminOpsKeys.referralCohorts(), params] as const,
  charityVerification: (claimId: string) =>
    [...adminOpsKeys.all, 'charity-verification', claimId] as const,
  // DM-8485
  websiteLeadOutreach: () => [...adminOpsKeys.all, 'website-lead-outreach'] as const,
  websiteLeadOutreachLog: (leadType: WebsiteLeadType, id: string) =>
    [...adminOpsKeys.websiteLeadOutreach(), leadType, id] as const,
};

/**
 * A send moves through expanding -> sending in seconds to minutes, so the
 * detail view polls while it is live and stops once it is not. The console's
 * only other timer is a flat 60s badge poll, which is both too slow to watch a
 * send and pure waste once one has finished.
 */
const BROADCAST_LIVE_POLL_MS = 5000;

const BROADCAST_TERMINAL_STATUSES: readonly BroadcastStatus[] = [
  'completed',
  'cancelled',
  'failed',
];

const OVERVIEW_POLL_MS = 60000;

// ============================================================================
// Queries
// ============================================================================

export function useOverviewBadges(enabled = true) {
  return useQuery({
    queryKey: adminOpsKeys.overview(),
    queryFn: () => adminOpsApi.getOverview(),
    refetchInterval: OVERVIEW_POLL_MS,
    enabled,
  });
}

/**
 * Cursor-paginated queue built on useInfiniteQuery; `rows` flattens all
 * fetched pages for direct use with QueueTable.
 */
export function useAdminQueue<T>(
  name: AdminQueueName,
  fetchPage: (cursor: string | undefined) => Promise<ListEnvelope<T>>,
  params: unknown = {}
) {
  const query = useInfiniteQuery({
    queryKey: adminOpsKeys.queuePage(name, params),
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ListEnvelope<T>) => lastPage.meta.next_cursor ?? undefined,
  });

  const rows = useMemo(() => query.data?.pages.flatMap(page => page.data) ?? [], [query.data]);

  return { ...query, rows };
}

export function useModerationReports(params: ListParams = {}) {
  return useAdminQueue<ModerationReportItem>(
    'moderation-reports',
    cursor =>
      adminOpsApi.listModerationQueue('reports', { ...params, cursor }) as Promise<
        ListEnvelope<ModerationReportItem>
      >,
    params
  );
}

export function useFlaggedContent(params: ListParams = {}) {
  return useAdminQueue<FlaggedContentItem>(
    'moderation-flagged',
    cursor =>
      adminOpsApi.listModerationQueue('flagged', { ...params, cursor }) as Promise<
        ListEnvelope<FlaggedContentItem>
      >,
    params
  );
}

export function useAuditEvents(params: AuditEventParams = {}) {
  return useAdminQueue<AuditEventItem>(
    'audit-events',
    cursor => adminOpsApi.listAuditEvents({ ...params, cursor }),
    params
  );
}

export function useApprovals(params: ListParams & { status?: string } = {}) {
  return useAdminQueue<PendingActionItem>(
    'approvals',
    cursor => adminOpsApi.listApprovals({ ...params, cursor }),
    params
  );
}

export function useIrsReviews(params: ListParams & { pending_only?: string } = {}) {
  return useAdminQueue<IrsSyncReviewItem>(
    'irs-reviews',
    cursor => adminOpsApi.listIrsReviews({ ...params, cursor }),
    params
  );
}

export function useDeletionRequests(params: ListParams & { status?: string } = {}) {
  return useAdminQueue<DeletionRequestItem>(
    'deletion-requests',
    cursor => adminOpsApi.listDeletionRequests({ ...params, cursor }),
    params
  );
}

export function useSupportRequests(params: ListParams & { status?: SupportStatus } = {}) {
  return useAdminQueue<SupportRequestItem>(
    'support-requests',
    cursor => adminOpsApi.listSupportRequests({ ...params, cursor }),
    params
  );
}

export function useQuarantine(params: ListParams = {}) {
  return useAdminQueue<QuarantineItem>(
    'quarantine',
    cursor => adminOpsApi.listQuarantine({ ...params, cursor }),
    params
  );
}

export function useFailedOutbox(params: ListParams = {}) {
  return useAdminQueue<FailedOutboxItem>(
    'outbox-failed',
    cursor => adminOpsApi.listFailedOutbox({ ...params, cursor }),
    params
  );
}

export function useStrandedAvatars(params: ListParams & { older_than_minutes?: number } = {}) {
  return useAdminQueue<StrandedAvatarItem>(
    'avatars-stranded',
    cursor => adminOpsApi.listStrandedAvatars({ ...params, cursor }),
    params
  );
}

export function useOrgStatusUnacknowledged(params: ListParams = {}) {
  return useAdminQueue<OrgStatusUnacknowledgedItem>(
    'org-status',
    cursor => adminOpsApi.listOrgStatusUnacknowledged({ ...params, cursor }),
    params
  );
}

export function useOcrManualReview(params: ListParams = {}) {
  return useAdminQueue<OcrManualReviewItem>(
    'ocr-manual-review',
    cursor => adminOpsApi.listOcrManualReview({ ...params, cursor }),
    params
  );
}

/**
 * DM-5804: donors awaiting fraud review. Entity-centric — one row per donor,
 * with their flagged donations fetched separately when a case is opened.
 */
export function useFraudReviews(params: ListParams = {}) {
  return useAdminQueue<FraudReviewDonorItem>(
    'fraud-review',
    cursor => adminOpsApi.listFraudReviews({ ...params, cursor }),
    params
  );
}

/** DM-5804: the flagged donations backing one donor's case. */
export function useFraudReviewDonor(userId: string | null) {
  return useQuery({
    queryKey: adminOpsKeys.fraudReviewDonor(userId ?? ''),
    queryFn: () => adminOpsApi.getFraudReviewDonor(userId as string),
    enabled: userId !== null,
  });
}

export function useComplianceRegister() {
  return useQuery({
    queryKey: adminOpsKeys.compliance(),
    queryFn: () => adminOpsApi.getComplianceRegister(),
  });
}

export function useUserEnforcement(userId: string) {
  return useQuery({
    queryKey: adminOpsKeys.enforcement(userId),
    queryFn: () => adminOpsApi.getUserEnforcement(userId),
    enabled: userId !== '',
  });
}

// ============================================================================
// Mutations — every action invalidates its owning queue + the overview badges
// and reports through the toast channel.
// ============================================================================

/** Maker-checker 202 responses carry a pending_action_id instead of a result. */
function isPendingActionResponse(data: unknown): boolean {
  if (typeof data !== 'object' || data === null || !('data' in data)) return false;
  const inner = (data as { data: unknown }).data;
  return typeof inner === 'object' && inner !== null && 'pending_action_id' in inner;
}

interface AdminOpsMutationConfig<TVariables, TData> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage: string | ((data: TData, variables: TVariables) => string);
  invalidate: (variables: TVariables) => QueryKey[];
}

function useAdminOpsMutation<TVariables, TData>({
  mutationFn,
  successMessage,
  invalidate,
}: AdminOpsMutationConfig<TVariables, TData>) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      for (const key of invalidate(variables)) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      queryClient.invalidateQueries({ queryKey: adminOpsKeys.overview() });
      toast.success(
        typeof successMessage === 'function' ? successMessage(data, variables) : successMessage
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useModerationAction() {
  return useAdminOpsMutation({
    mutationFn: (input: Parameters<typeof adminOpsApi.moderationAction>[0]) =>
      adminOpsApi.moderationAction(input),
    successMessage: (_data, { action }) => `Moderation action ${action} applied`,
    invalidate: () => [
      adminOpsKeys.queue('moderation-reports'),
      adminOpsKeys.queue('moderation-flagged'),
    ],
  });
}

/** DM-7283: closing the reports filed against a user (Reports tab, USER rows). */
export function useResolveUserReports() {
  return useAdminOpsMutation({
    mutationFn: ({
      userId,
      action,
      reason,
      note,
    }: {
      userId: string;
      action: UserReportResolutionAction;
      reason: string;
      note?: string;
    }) => adminOpsApi.resolveUserReports(userId, action, reason, note),
    successMessage: (data, { action }) => userReportResolutionMessage(data.data, action),
    invalidate: ({ userId }) => [
      adminOpsKeys.queue('moderation-reports'),
      adminOpsKeys.queue('approvals'),
      adminOpsKeys.enforcement(userId),
    ],
  });
}

export function useDecideApproval() {
  return useAdminOpsMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: ApprovalDecision;
      note?: string;
    }) => adminOpsApi.decideApproval(id, decision, note),
    successMessage: (_data, { decision }) =>
      decision === 'approved' ? 'Action approved and executed' : 'Action rejected',
    // A broadcast.send decision moves the broadcast itself (to scheduled, or
    // back to draft on a rejection), so its queue and any open detail have to
    // be refetched too -- the decision can now be made from the Broadcasts
    // page, where a stale row is the only thing the operator is looking at.
    invalidate: () => [
      adminOpsKeys.queue('approvals'),
      adminOpsKeys.queue('broadcasts'),
      // Prefix keys: every open broadcast detail and every cached approval
      // lookup, without needing the broadcast id the payload holds.
      [...adminOpsKeys.all, 'broadcast'],
      [...adminOpsKeys.all, 'broadcast-approval'],
      adminOpsKeys.queue('audit-legal-holds'),
      adminOpsKeys.queue('audit-prune-approvals'),
    ],
  });
}

export function useWithdrawApproval() {
  return useAdminOpsMutation({
    mutationFn: ({ id }: { id: string }) => adminOpsApi.withdrawApproval(id),
    successMessage: 'Pending action withdrawn',
    invalidate: () => [adminOpsKeys.queue('approvals')],
  });
}

export function useResolveIrsReview() {
  return useAdminOpsMutation({
    mutationFn: ({
      id,
      resolution,
      notes,
    }: {
      id: string;
      resolution: IrsReviewResolution;
      notes?: string;
    }) => adminOpsApi.resolveIrsReview(id, resolution, notes),
    successMessage: (_data, { resolution }) => `IRS review marked ${resolution}`,
    invalidate: () => [adminOpsKeys.queue('irs-reviews')],
  });
}

export function usePlaceLegalHold() {
  return useAdminOpsMutation({
    mutationFn: ({
      deletionRequestId,
      exemptionBasis,
      notes,
    }: {
      deletionRequestId: string;
      exemptionBasis: ExemptionBasis;
      notes?: string;
    }) => adminOpsApi.placeLegalHold(deletionRequestId, exemptionBasis, notes),
    successMessage: 'Legal hold submitted for dual-control approval',
    invalidate: () => [adminOpsKeys.queue('deletion-requests'), adminOpsKeys.queue('approvals')],
  });
}

// DM-7703: website leads. The first page's meta carries the whole-table summary.
export function useEarlyAccessLeads(params: EarlyAccessLeadParams = {}) {
  const queue = useAdminQueue<EarlyAccessLeadItem>(
    'early-access-leads',
    cursor => adminOpsApi.listEarlyAccessLeads({ ...params, cursor }),
    params
  );
  const firstPage = queue.data?.pages[0] as
    SummarizedListEnvelope<EarlyAccessLeadItem, EarlyAccessLeadsSummary> | undefined;
  return { ...queue, summary: firstPage?.meta.summary };
}

export function useFoundingPartnerRequests(params: FoundingPartnerRequestParams = {}) {
  const queue = useAdminQueue<FoundingPartnerRequestItem>(
    'founding-partner-requests',
    cursor => adminOpsApi.listFoundingPartnerRequests({ ...params, cursor }),
    params
  );
  const firstPage = queue.data?.pages[0] as
    SummarizedListEnvelope<FoundingPartnerRequestItem, FoundingPartnerRequestsSummary> | undefined;
  return { ...queue, summary: firstPage?.meta.summary };
}

export function useUpdateFoundingPartnerStatus() {
  return useAdminOpsMutation({
    mutationFn: ({ id, status }: { id: string; status: FoundingPartnerStatus }) =>
      adminOpsApi.updateFoundingPartnerStatus(id, status),
    successMessage: 'Founding Partner status updated',
    invalidate: () => [adminOpsKeys.queue('founding-partner-requests')],
  });
}

// DM-8485: the outreach log behind one lead, newest first. Fetched only once
// a lead is open: each read is a privileged look at personal data.
export function useWebsiteLeadOutreach(leadType: WebsiteLeadType, id: string | null) {
  return useQuery({
    queryKey: adminOpsKeys.websiteLeadOutreachLog(leadType, id ?? ''),
    queryFn: () => adminOpsApi.listWebsiteLeadOutreach(leadType, id ?? ''),
    enabled: id !== null,
  });
}

/** Appends one event; the list rows carry the summary, so both queues refresh. */
export function useLogWebsiteLeadOutreach() {
  return useAdminOpsMutation({
    mutationFn: ({ id, body }: { id: string; body: LogWebsiteLeadOutreachPayload }) =>
      adminOpsApi.logWebsiteLeadOutreach(id, body),
    successMessage: 'Outreach logged',
    invalidate: ({ id, body }) => [
      adminOpsKeys.queue('early-access-leads'),
      adminOpsKeys.queue('founding-partner-requests'),
      adminOpsKeys.websiteLeadOutreachLog(body.lead_type, id),
    ],
  });
}

/** The stamped mail-merge sheet back in: one email_sent per row. */
export function useImportWebsiteLeadOutreach() {
  return useAdminOpsMutation({
    mutationFn: (body: ImportWebsiteLeadOutreachPayload) =>
      adminOpsApi.importWebsiteLeadOutreach(body),
    successMessage: data => importResultLine(data.data),
    invalidate: () => [
      adminOpsKeys.queue('early-access-leads'),
      adminOpsKeys.queue('founding-partner-requests'),
      adminOpsKeys.websiteLeadOutreach(),
    ],
  });
}

export function useUpdateSupportRequest() {
  return useAdminOpsMutation({
    mutationFn: ({
      id,
      update,
    }: {
      id: string;
      update: { status?: SupportStatus; external_ref?: string | null };
    }) => adminOpsApi.updateSupportRequest(id, update),
    successMessage: 'Support request updated',
    invalidate: () => [adminOpsKeys.queue('support-requests')],
  });
}

export function useAttestVendorDpa() {
  return useAdminOpsMutation({
    mutationFn: ({
      slug,
      nextReviewDueAt,
      notes,
    }: {
      slug: string;
      nextReviewDueAt: string;
      notes?: string;
    }) => adminOpsApi.attestVendorDpa(slug, nextReviewDueAt, notes),
    successMessage: (_data, { slug }) => `DPA review recorded for ${slug}`,
    invalidate: () => [adminOpsKeys.compliance()],
  });
}

export function useDecideQuarantine() {
  return useAdminOpsMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminOpsApi.decideQuarantine(id, reason),
    successMessage: 'Quarantined email discarded',
    invalidate: () => [adminOpsKeys.queue('quarantine')],
  });
}

/**
 * DM-5805: close a donor's case with a recorded conclusion.
 *
 * Invalidates the queue AND the overview badge, because resolving removes the
 * donor from both. ESCALATE answers 202 and opens a pending action rather than
 * acting, so the approvals queue is invalidated too.
 */
export function useResolveFraudReview() {
  return useAdminOpsMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: ResolveFraudReviewPayload }) =>
      adminOpsApi.resolveFraudReview(userId, payload),
    successMessage: (_data, variables) =>
      variables.payload.disposition === 'ESCALATE'
        ? 'Escalation opened — a second admin must approve it'
        : 'Fraud review resolved',
    invalidate: () => [
      adminOpsKeys.queue('fraud-review'),
      adminOpsKeys.overview(),
      adminOpsKeys.queue('approvals'),
    ],
  });
}

export function useReplayOutbox() {
  return useAdminOpsMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      adminOpsApi.replayOutbox(ids, reason),
    successMessage: data => `Replayed ${data.data.replayed} event(s)`,
    invalidate: () => [adminOpsKeys.queue('outbox-failed')],
  });
}

export function useReplayAllOutbox() {
  return useAdminOpsMutation({
    mutationFn: ({ reason }: { reason: string }) => adminOpsApi.replayAllOutbox(reason),
    successMessage: 'Replay-all submitted for dual-control approval',
    invalidate: () => [adminOpsKeys.queue('outbox-failed'), adminOpsKeys.queue('approvals')],
  });
}

export function useEnforceUser() {
  return useAdminOpsMutation({
    mutationFn: ({
      userId,
      action,
      reason,
      durationDays,
    }: {
      userId: string;
      action: EnforcementAction;
      reason: string;
      durationDays?: number;
    }) => adminOpsApi.enforceUser(userId, action, reason, durationDays),
    successMessage: (data, { action }) =>
      isPendingActionResponse(data)
        ? `${action} submitted for dual-control approval`
        : `${action} applied`,
    invalidate: ({ userId }) => [adminOpsKeys.enforcement(userId), adminOpsKeys.queue('approvals')],
  });
}

export function useDecideOcrManualReview() {
  return useAdminOpsMutation({
    mutationFn: ({
      documentId,
      action,
      note,
      expectedStatus,
    }: {
      documentId: string;
      action: 'approve' | 'reject';
      note?: string;
      expectedStatus?: OcrReviewHoldStatus;
    }) => adminOpsApi.decideOcrManualReview(documentId, action, note, expectedStatus),
    successMessage: (_data, { action }) =>
      action === 'approve' ? 'Document approved' : 'Document rejected',
    invalidate: () => [adminOpsKeys.queue('ocr-manual-review')],
  });
}

export function useBroadcasts(params: ListParams & { status?: string } = {}) {
  return useAdminQueue<BroadcastItem>(
    'broadcasts',
    cursor => adminOpsApi.listBroadcasts({ ...params, cursor }),
    params
  );
}

/** Polls only while the broadcast is still moving. */
export function useBroadcast(id: string | null) {
  return useQuery({
    queryKey: adminOpsKeys.broadcast(id ?? ''),
    queryFn: () => adminOpsApi.getBroadcast(id as string),
    enabled: id !== null,
    refetchInterval: query => {
      const status = query.state.data?.data.status;
      if (status === undefined || BROADCAST_TERMINAL_STATUSES.includes(status)) {
        return false;
      }
      return BROADCAST_LIVE_POLL_MS;
    },
  });
}

export function useBroadcastAudiencePreview(audience: string, enabled = true) {
  return useQuery({
    queryKey: adminOpsKeys.broadcastAudience(audience),
    queryFn: () => adminOpsApi.previewBroadcastAudience(audience),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateBroadcast() {
  return useAdminOpsMutation({
    mutationFn: (payload: CreateBroadcastPayload) => adminOpsApi.createBroadcast(payload),
    successMessage: 'Draft created',
    invalidate: () => [adminOpsKeys.queue('broadcasts')],
  });
}

export function useUpdateBroadcast() {
  return useAdminOpsMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBroadcastPayload }) =>
      adminOpsApi.updateBroadcast(id, payload),
    successMessage: 'Broadcast updated',
    invalidate: ({ id }) => [adminOpsKeys.queue('broadcasts'), adminOpsKeys.broadcast(id)],
  });
}

export function useTestBroadcast() {
  return useAdminOpsMutation({
    mutationFn: ({ id, targetEmail }: { id: string; targetEmail: string }) =>
      adminOpsApi.testBroadcast(id, targetEmail),
    successMessage: data =>
      `Test queued for ${data.data.devices_targeted} device(s) on ${data.data.target_email}`,
    invalidate: ({ id }) => [adminOpsKeys.broadcast(id)],
  });
}

/**
 * The open approval request for one broadcast, or null when there is none.
 *
 * Read off the approvals queue rather than the broadcast: the pending action
 * carries the broadcast id in its payload, and there is no route that returns
 * one by broadcast. The queue is cursor-paginated and mixes every operation,
 * so this walks pages rather than trusting the first -- a request pushed off
 * page one read as "no request" would hide the decision controls from the
 * operator who has to decide.
 */
export function useBroadcastApproval(broadcastId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: adminOpsKeys.broadcastApproval(broadcastId ?? ''),
    queryFn: () =>
      findPendingApproval(broadcastId as string, cursor =>
        adminOpsApi.listApprovals({
          status: 'pending',
          limit: APPROVAL_LOOKUP_PAGE_SIZE,
          ...(cursor === undefined ? {} : { cursor }),
        })
      ),
    enabled: broadcastId !== null && enabled,
    staleTime: 5_000,
  });
}

export function useRequestBroadcastApproval() {
  return useAdminOpsMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminOpsApi.requestBroadcastApproval(id, reason),
    successMessage: 'Submitted for dual-control approval',
    invalidate: ({ id }) => [
      adminOpsKeys.queue('broadcasts'),
      adminOpsKeys.broadcast(id),
      // The request lands on the approvals queue, which a second operator is
      // very likely to be looking at right now.
      adminOpsKeys.queue('approvals'),
    ],
  });
}

export function useCancelBroadcast() {
  return useAdminOpsMutation({
    mutationFn: (id: string) => adminOpsApi.cancelBroadcast(id),
    successMessage: 'Broadcast cancelled',
    invalidate: id => [adminOpsKeys.queue('broadcasts'), adminOpsKeys.broadcast(id)],
  });
}

// ============================================================================
// DM-7185 account deletion
// ============================================================================

export function useAccountDeletionRequests(
  params: ListParams & { status?: AccountDeletionWebRequestStatus } = {}
) {
  return useAdminQueue<AccountDeletionWebRequestItem>(
    'account-deletion-requests',
    cursor => adminOpsApi.listAccountDeletionRequests({ ...params, cursor }),
    params
  );
}

/**
 * One account's current deletion state. The route 404s for an unknown id, so
 * callers must render `isError` as "could not load" and never as "nothing
 * scheduled" -- only a successful `active: false` means that.
 */
export function useUserDeletion(userId: string | null) {
  return useQuery({
    queryKey: adminOpsKeys.userDeletion(userId ?? ''),
    queryFn: () => adminOpsApi.getUserDeletion(userId as string),
    enabled: userId !== null && userId !== '',
  });
}

/**
 * The open `user.delete` request for one account, found by walking the
 * approvals queue. Submitting a deletion only parks a request, so the
 * account's deletion state keeps reading "nothing scheduled" -- this is the
 * only way the page can know one is already waiting, and the backend does not
 * deduplicate them.
 */
export function useUserDeletionApproval(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: adminOpsKeys.userDeletionApproval(userId),
    queryFn: () =>
      walkPendingApprovals(
        actions => pendingUserDeletionFor(actions, userId),
        cursor =>
          adminOpsApi.listApprovals({
            status: 'pending',
            limit: APPROVAL_LOOKUP_PAGE_SIZE,
            ...(cursor === undefined ? {} : { cursor }),
          }),
        PENDING_DELETION_LOOKUP_EXHAUSTED
      ),
    enabled: userId !== '' && enabled,
    staleTime: 5_000,
  });
}

/**
 * A mutation although it is a GET: every lookup, hit or miss, is written to
 * the audit ledger, so it must run only when the operator presses the button
 * -- never on a retry, a refetch or a remount, which a query would do.
 *
 * Deliberately NOT through useAdminOpsMutation: the result is rendered inline
 * beside the field, and nothing about an email address belongs in a toast.
 */
export function useLookupUserByEmail() {
  return useMutation({
    mutationFn: (email: string) => adminOpsApi.lookupUserByEmail(email),
  });
}

export function useRequestUserDeletion() {
  return useAdminOpsMutation({
    mutationFn: ({
      userId,
      reason,
      note,
    }: {
      userId: string;
      reason: UserDeletionReason;
      note: string;
    }) => adminOpsApi.requestUserDeletion(userId, reason, note),
    // Fixed copy: the toast never echoes the note or anything about the donor.
    successMessage: data =>
      isPendingActionResponse(data)
        ? 'Account deletion submitted for dual-control approval'
        : 'Account deletion requested',
    invalidate: ({ userId }) => [
      adminOpsKeys.queue('account-deletion-requests'),
      adminOpsKeys.userDeletion(userId),
      adminOpsKeys.userDeletionApproval(userId),
      adminOpsKeys.queue('approvals'),
    ],
  });
}

export function useCancelUserDeletion() {
  return useAdminOpsMutation({
    mutationFn: ({ userId, note }: { userId: string; note: string }) =>
      adminOpsApi.cancelUserDeletion(userId, note),
    successMessage: 'Account deletion cancelled',
    invalidate: ({ userId }) => [
      adminOpsKeys.queue('account-deletion-requests'),
      adminOpsKeys.userDeletion(userId),
      // Cancelling restores the status the account held before the deletion.
      adminOpsKeys.enforcement(userId),
      adminOpsKeys.queue('approvals'),
    ],
  });
}

// ============================================================================
// DM-7490 referral codes and Founding Partner cohorts
// ============================================================================

export function useReferralCodes(status: ReferralCodeStatusFilter = 'all') {
  return useQuery({
    queryKey: adminOpsKeys.referralCodeList(status),
    queryFn: () => adminOpsApi.listReferralCodes(status),
  });
}

export function useReferralCohorts(params: ReferralCohortParams, enabled = true) {
  return useQuery({
    queryKey: adminOpsKeys.referralCohortReport(params),
    queryFn: () => adminOpsApi.getReferralCohorts(params),
    enabled,
  });
}

/** Code changes move both the list and any report grouped by code. */
function referralKeys(): QueryKey[] {
  return [adminOpsKeys.referralCodes(), adminOpsKeys.referralCohorts()];
}

export function useCreateReferralCode() {
  return useAdminOpsMutation({
    mutationFn: (payload: CreateReferralCodePayload) => adminOpsApi.createReferralCode(payload),
    successMessage: data => `Created ${data.code.code}`,
    invalidate: referralKeys,
  });
}

export function useSetReferralCodeStatus() {
  return useAdminOpsMutation({
    mutationFn: ({ id, active, reason }: { id: string; active: boolean; reason: string }) =>
      adminOpsApi.setReferralCodeStatus(id, active, reason),
    successMessage: data => `${data.code.code} is now ${data.code.active ? 'active' : 'inactive'}`,
    invalidate: referralKeys,
  });
}

export function useRotateReferralCode() {
  return useAdminOpsMutation({
    mutationFn: ({ id, newCode, reason }: { id: string; newCode: string; reason: string }) =>
      adminOpsApi.rotateReferralCode(id, newCode, reason),
    successMessage: data => `Rotated ${data.previous.code} to ${data.code.code}`,
    invalidate: referralKeys,
  });
}

export function useAuditLegalHolds(params: AuditLegalHoldParams = {}) {
  return useAdminQueue<AuditLegalHoldItem>(
    'audit-legal-holds',
    cursor => adminOpsApi.listAuditLegalHolds({ ...params, cursor }),
    params
  );
}

export function useAuditPruneApprovals(params: AuditPruneApprovalParams = {}) {
  return useAdminQueue<AuditPruneApprovalItem>(
    'audit-prune-approvals',
    cursor => adminOpsApi.listAuditPruneApprovals({ ...params, cursor }),
    params
  );
}

function auditLegalHoldKeys(): QueryKey[] {
  return [adminOpsKeys.queue('audit-legal-holds'), adminOpsKeys.queue('approvals')];
}

export function usePlaceAuditLegalHold() {
  return useAdminOpsMutation({
    mutationFn: (payload: PlaceAuditLegalHoldPayload) => adminOpsApi.placeAuditLegalHold(payload),
    successMessage: 'Legal hold submitted for dual-control approval',
    invalidate: auditLegalHoldKeys,
  });
}

export function useExtendAuditLegalHold() {
  return useAdminOpsMutation({
    mutationFn: ({ holdId, payload }: { holdId: string; payload: ExtendAuditLegalHoldPayload }) =>
      adminOpsApi.extendAuditLegalHold(holdId, payload),
    successMessage: 'Hold extension submitted for dual-control approval',
    invalidate: auditLegalHoldKeys,
  });
}

export function useReleaseAuditLegalHold() {
  return useAdminOpsMutation({
    mutationFn: ({ holdId, payload }: { holdId: string; payload: ReleaseAuditLegalHoldPayload }) =>
      adminOpsApi.releaseAuditLegalHold(holdId, payload),
    successMessage: 'Hold release submitted for dual-control approval',
    invalidate: auditLegalHoldKeys,
  });
}

export function useDecideAuditPruneApproval() {
  return useAdminOpsMutation({
    mutationFn: ({
      approvalId,
      decision,
      decisionRef,
    }: {
      approvalId: string;
      decision: AuditPruneDecision;
      decisionRef: string;
    }) => adminOpsApi.decideAuditPruneApproval(approvalId, decision, decisionRef),
    successMessage: data =>
      isPendingActionResponse(data)
        ? 'Prune approval submitted: a second admin must approve it in Approvals'
        : 'Prune request rejected',
    invalidate: () => [
      adminOpsKeys.queue('audit-prune-approvals'),
      adminOpsKeys.queue('approvals'),
    ],
  });
}

export function useResendAuditPruneDecision() {
  return useAdminOpsMutation({
    mutationFn: ({
      approvalId,
      decision,
      decisionRef,
    }: {
      approvalId: string;
      decision: AuditPruneDecision;
      decisionRef: string;
    }) => adminOpsApi.decideAuditPruneApproval(approvalId, decision, decisionRef),
    successMessage: 'Recorded decision resent to the retention workflow',
    invalidate: () => [adminOpsKeys.queue('audit-prune-approvals')],
  });
}

// ============================================================================
// DM-7976 charity verification
// ============================================================================

export function useCharityVerifications(params: CharityVerificationListParams = {}) {
  return useAdminQueue<CharityVerificationQueueItem>(
    'charity-verifications',
    cursor => adminOpsApi.listCharityVerifications({ ...params, cursor }),
    params
  );
}

/**
 * One claim's evidence. Each read is a privileged read written to the audit
 * ledger, so it runs only for the claim the reviewer opened. The document
 * links it carries last 60 seconds; the page's Re-fetch calls `refetch`.
 */
export function useCharityVerification(claimId: string | null) {
  return useQuery({
    queryKey: adminOpsKeys.charityVerification(claimId ?? ''),
    queryFn: () => adminOpsApi.getCharityVerification(claimId as string),
    enabled: claimId !== null && claimId !== '',
  });
}

export interface CharityVerificationDecisionInput {
  claimId: string;
  payload: CharityVerificationDecisionPayload;
}

/**
 * Records a decision. The queue row and the open detail both change (status,
 * allowed actions, a parked second-operator approval), so both are refetched.
 * The route's 409/422 answers are explained inline by the page from the
 * error's code; the toast here still names them.
 */
export function useDecideCharityVerification() {
  return useAdminOpsMutation({
    mutationFn: ({ claimId, payload }: CharityVerificationDecisionInput) =>
      adminOpsApi.decideCharityVerification(claimId, payload),
    successMessage: (data, { payload }) => decisionSuccessMessage(data.data, payload.decision),
    invalidate: ({ claimId }) => [
      adminOpsKeys.queue('charity-verifications'),
      adminOpsKeys.charityVerification(claimId),
    ],
  });
}
